import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import PropTypes from 'prop-types';

import ChevronDownIcon from '../icons/chevrondown.svg?react';
import MarkdownRenderer from './MarkdownRenderer.jsx';
import Spinner from '../Spinner.jsx';
import GlobeIcon from '../icons/globe.svg?react';
import AgentsIcon from '../icons/agents.svg?react';
import FileIcon from '../icons/file.svg?react';
import TerminalIcon from '../icons/terminal.svg?react';
import FunctionIcon from '../icons/function.svg?react';

/* ── Tool config registry ── */

const TOOL_CONFIG = {
    // File operations
    read_file:      { icon: FileIcon,     done: 'Read',         active: 'Reading',        context: 'path' },
    edit_file:      { icon: FileIcon,     done: 'Edited',       active: 'Editing',        context: 'path' },
    create_file:    { icon: FileIcon,     done: 'Created',      active: 'Creating',       context: 'path' },
    list_directory: { icon: FileIcon,     done: 'Listed',       active: 'Listing',        context: 'path' },
    grep:           { icon: FileIcon,     done: 'Searched for', active: 'Searching for',  context: 'pattern' },
    // Terminal
    bash:           { icon: TerminalIcon, done: 'Ran command',  active: 'Running command...',  context: 'command' },
    // Web
    web_search:     { icon: GlobeIcon,    done: 'Searched',     active: 'Searching',      context: 'query' },
    web_fetch:      { icon: GlobeIcon,    done: 'Fetched',      active: 'Fetching',       context: 'url' },
};

const DEFAULT_CONFIG = { icon: FunctionIcon, done: 'Executed', active: 'Executing', context: null };

function getConfig(part) {
    // Sub-agent has its own rendering path
    if (part.tool_type === 'sub_agent') return null;
    return TOOL_CONFIG[part.name] || DEFAULT_CONFIG;
}

/* ── Helpers ── */

function parseParams(raw) {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch { return null; }
}

function getContextValue(params, contextKey) {
    if (!params) return null;
    // Try the configured key first, then fall back to first string value
    if (contextKey && params[contextKey]) {
        const val = params[contextKey];
        return typeof val === 'string' ? val : null;
    }
    for (const val of Object.values(params)) {
        if (typeof val === 'string' && val.length > 0) return val;
    }
    return null;
}

function getHeaderText(config, hasResult, context) {
    const verb = hasResult ? config.done : config.active;
    if (context) return `${verb} ${context}`;
    return verb;
}

const MAX_RESULT_LINES = 10;

function truncateResult(text) {
    if (!text) return { full: null, truncated: null, truncatedCount: 0 };
    const lines = text.split('\n');
    if (lines.length <= MAX_RESULT_LINES) {
        return { full: text, truncated: null, truncatedCount: 0 };
    }
    const truncatedCount = lines.length - MAX_RESULT_LINES;
    const visible = lines.slice(truncatedCount);
    return {
        full: text,
        truncated: visible.join('\n'),
        truncatedCount,
    };
}

/* ── Sub-agent trace (only nested component) ── */

const SubAgentTrace = ({ part, hasResult, reduceMotion }) => {
    const [expandedThinking, setExpandedThinking] = useState(new Set());
    const trace = part.toolResult?.trace;
    const streamingContent = part.streaming_content || null;
    const activity = part.streaming_activity || [];

    const answerItems = useMemo(() => {
        const validTrace = trace && trace.length > 0 ? trace : null;
        return validTrace?.filter(i => i.type === 'answer') || [];
    }, [trace]);

    return (
        <div className="space-y-spacing-2 p-spacing-2" onClick={e => e.stopPropagation()}>
            {/* Interleaved activity (thinking + searches in chronological order) */}
            {activity.length > 0 && (
                <div className="space-y-spacing-2">
                    {activity.map((item, idx) => {
                        if (item.type === 'tool') {
                            const toolConfig = TOOL_CONFIG[item.name] || DEFAULT_CONFIG;
                            const ToolIcon = toolConfig.icon;
                            const context = getContextValue(item.parameters, toolConfig.context);
                            const verb = item.status === 'done' ? toolConfig.done : toolConfig.active;
                            const label = context ? `${verb} ${context}` : verb;
                            return (
                                <div key={`activity-${idx}`} className="flex items-center gap-spacing-2 text-secondary text-sm">
                                    {item.status === 'active'
                                        ? <Spinner size="xs" color="text-secondary" />
                                        : <ToolIcon className="size-[14px] flex-shrink-0" />
                                    }
                                    <span className="truncate">{label}</span>
                                </div>
                            );
                        }
                        if (item.type === 'thinking') {
                            const isExpanded = expandedThinking.has(idx);
                            const toggleThinking = () => setExpandedThinking(prev => {
                                const next = new Set(prev);
                                next.has(idx) ? next.delete(idx) : next.add(idx);
                                return next;
                            });
                            return (
                                <div key={`activity-${idx}`}>
                                    <button
                                        type="button"
                                        className="hover:cursor-pointer flex items-center gap-spacing-1 text-left focus:outline-none"
                                        onClick={toggleThinking}
                                    >
                                        {item.status === 'active'
                                            ? <Spinner size="xs" color="text-secondary" />
                                            : <ChevronDownIcon className={`size-[14px] text-secondary transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                                        }
                                        <span className="text-secondary text-sm">
                                            {item.status === 'active' ? 'Thinking' : item.duration_ms ? `Thought for ${item.duration_ms.toFixed(2)}s` : 'Thought'}
                                        </span>
                                    </button>
                                    <AnimatePresence>
                                        {isExpanded && item.content && (
                                            <motion.div
                                                initial={reduceMotion ? false : { opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: reduceMotion ? 0 : 0.3, ease: 'easeInOut' }}
                                                className="overflow-hidden"
                                            >
                                                <div className="mt-spacing-1 pl-spacing-4 text-secondary text-sm whitespace-pre-wrap">
                                                    {item.content}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        }
                        return null;
                    })}
                </div>
            )}

            {/* Answer content — streaming or from trace */}
            {answerItems.length > 0 && (
                <div className="text-primary text-sm">
                    <MarkdownRenderer content={answerItems.map(i => i.content).join('')} />
                </div>
            )}
            {!hasResult && streamingContent && (
                <div className="text-primary text-sm">
                    <MarkdownRenderer content={streamingContent} />
                </div>
            )}
        </div>
    );
};

/* ── Main component ── */

const ToolUsePart = ({ part }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const userToggledRef = useRef(false);
    const shouldReduceMotion = useReducedMotion();
    const [showFullResult, setShowFullResult] = useState(false);

    const isSubAgent = part.tool_type === 'sub_agent';
    const config = getConfig(part);
    const result = part.toolResult?.result ?? null;
    const hasResult = result !== null;
    const params = useMemo(() => parseParams(part.parameters), [part.parameters]);
    const streamingContent = part.streaming_content || null;

    const isWebSearch = isSubAgent && part.name === 'web_search';

    // Auto-expand sub-agent while streaming
    useEffect(() => {
        if (isSubAgent && !userToggledRef.current) {
            const hasActivity = streamingContent
                || (part.streaming_activity && part.streaming_activity.length > 0);
            if (hasActivity) setIsExpanded(true);
        }
    }, [isSubAgent, streamingContent, part.streaming_activity]);

    // Header
    const Icon = isWebSearch ? GlobeIcon : isSubAgent ? AgentsIcon : config.icon;
    const headerText = useMemo(() => {
        if (isSubAgent) {
            if (isWebSearch) {
                const query = params?.query || null;
                if (hasResult) return query ? `Web search · ${query}` : 'Web search';
                return query ? `Searching the web for ${query}` : (part.instruction || 'Searching the web');
            }
            const name = (part.name || 'agent').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
            const ctx = getContextValue(params, null);
            if (hasResult) return ctx ? `${name} · ${ctx}` : name;
            return `Running ${name.toLowerCase()}...`;
        }
        const ctx = getContextValue(params, config.context);
        return getHeaderText(config, hasResult, ctx);
    }, [isSubAgent, isWebSearch, part.name, part.instruction, config, hasResult, params]);

    // Expandable?
    const expandable = isSubAgent
        ? (hasResult || !!streamingContent || (part.streaming_activity && part.streaming_activity.length > 0))
        : hasResult;

    const handleToggle = () => {
        userToggledRef.current = true;
        setIsExpanded(v => !v);
    };

    // Truncated result for non-sub-agent tools
    const truncation = useMemo(() => truncateResult(result), [result]);

    return (
        <div
            className="bg-bg-thinking rounded-lg p-spacing-2 w-full hover:cursor-pointer group"
            onClick={handleToggle}
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            aria-label="Toggle tool details"
        >
            <div className="flex items-start w-full text-secondary">
                <span className="text-dls-body flex items-start gap-spacing-2 group-hover:text-primary min-w-0">
                    {!hasResult
                        ? <Icon className="size-[14px] flex-shrink-0 mt-[3px] animate-pulse" />
                        : <Icon className="size-[14px] flex-shrink-0 mt-[3px]" />
                    }
                    <span className="truncate">{headerText}</span>
                </span>
                {expandable && (
                    <span className="ml-auto flex-shrink-0 mt-[3px] group-hover:text-primary" aria-hidden="true">
                        <ChevronDownIcon className={`size-[14px] transition-transform duration-300 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                    </span>
                )}
            </div>

            <AnimatePresence>
                {isExpanded && expandable && (
                    <motion.div
                        initial={shouldReduceMotion ? false : { opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: shouldReduceMotion ? 0 : 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                    >
                        {isSubAgent ? (
                            <SubAgentTrace part={part} hasResult={hasResult} reduceMotion={shouldReduceMotion} />
                        ) : truncation.full && (
                            <div className="pt-spacing-2" onClick={e => e.stopPropagation()}>
                                <pre className="text-dls-body text-secondary whitespace-pre-wrap break-all font-mono">
                                    {showFullResult ? truncation.full : (truncation.truncated || truncation.full)}
                                </pre>
                                {truncation.truncatedCount > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowFullResult(v => !v)}
                                        className="mt-spacing-1 text-dls-small text-secondary hover:text-primary transition-colors duration-200"
                                    >
                                        {showFullResult ? 'Show less' : `Show ${truncation.truncatedCount} more lines`}
                                    </button>
                                )}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

ToolUsePart.propTypes = {
    part: PropTypes.shape({
        name: PropTypes.string,
        tool_type: PropTypes.string,
        parameters: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        toolResult: PropTypes.shape({
            result: PropTypes.string,
            trace: PropTypes.array,
            metadata: PropTypes.object,
        }),
        streaming_content: PropTypes.string,
        streaming_activity: PropTypes.array,
        instruction: PropTypes.string,
        sub_agent_model: PropTypes.string,
    }).isRequired,
};

export default ToolUsePart;
